/**
 * PWA bootstrap — service worker registration + update detection.
 *
 * Exposes two browser custom events:
 *   • "pwa:updateavailable" — fires when a new SW is installed and waiting
 *   • "pwa:offline"         — fires when the browser loses connectivity
 *   • "pwa:online"          — fires when the browser regains connectivity
 *
 * The InstallPrompt / UpdatePrompt React components listen for these events.
 *
 * Also fires a single best-effort analytics ping when the browser confirms
 * the PWA has been installed (`appinstalled`).
 */
import { api } from "@/lib/api";
import {
  getSource,
  getAnonymousId,
  detectInstallPlatform,
} from "@/lib/attribution";

let _waitingWorker = null;
let _installTracked = false;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Only register in production / non-dev environments — Create React App
  // dev server proxies the SW request and serves the wrong content.
  if (process.env.NODE_ENV === "development") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Check for updates every 60 minutes when the tab is open
        setInterval(() => registration.update().catch(() => null), 60 * 60 * 1000);

        // Listen for new SW installing
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              // A new version is waiting — let the UI tell the user.
              _waitingWorker = newWorker;
              window.dispatchEvent(new CustomEvent("pwa:updateavailable"));
            }
          });
        });
      })
      .catch(() => {
        /* SW registration failed — non-fatal, app still works as a normal SPA */
      });

    // Reload the page when the new SW takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });

  // Connectivity events
  window.addEventListener("online", () =>
    window.dispatchEvent(new CustomEvent("pwa:online"))
  );
  window.addEventListener("offline", () =>
    window.dispatchEvent(new CustomEvent("pwa:offline"))
  );

  // PWA install attribution — fire once per browser when the OS confirms
  // the app has been added to the home screen.
  window.addEventListener("appinstalled", () => {
    if (_installTracked) return;
    _installTracked = true;
    try {
      const lang = (navigator && navigator.language) || "";
      api
        .post("/analytics/install", {
          source: getSource() || "install_banner",
          anonymous_id: getAnonymousId(),
          locale: lang.slice(0, 2).toLowerCase(),
          platform: detectInstallPlatform(),
        })
        .catch(() => {});
    } catch {
      /* never blocks anything */
    }
  });
}

export function applyServiceWorkerUpdate() {
  if (_waitingWorker) {
    _waitingWorker.postMessage({ type: "SKIP_WAITING" });
  } else {
    // No waiting worker — just refresh to pick up any new code
    window.location.reload();
  }
}
